import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Stack, IconButton, Tooltip, Paper, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Divider, TextField, Autocomplete
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { shortAbsenceService, employeeService } from '../../services/api';
import StatusChip from '../StatusChip';
import { inStaffOnly } from '../../utils/staffStatus';

const WORK_START = 9; // 09:00
const WORK_END = 18;  // 18:00
const HOURS = Array.from({ length: WORK_END - WORK_START }, (_, i) => WORK_START + i);
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function getWeekDates(baseDate) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 5 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
}

function formatDate(d) {
  return d.toISOString().slice(0, 10);
}

function formatShortDate(d) {
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export default function ShortAbsenceMapWidget() {
  const theme = useTheme();
  const [weekOffset, setWeekOffset] = useState(0);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // History filters - all default to "All" (null/empty)
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [historyYear, setHistoryYear] = useState('');
  const [historyMonth, setHistoryMonth] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  const baseDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [weekOffset]);

  const weekDates = useMemo(() => getWeekDates(baseDate), [baseDate]);

  // Load employees for filter dropdown
  useEffect(() => {
    (async () => {
      try {
        const list = await employeeService.getAll();
        setEmployees(inStaffOnly(list));   // STAFF-S1
      } catch (e) {
        console.error('Failed to load employees:', e);
      }
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Get all approved absences for the visible week's year
      const year = weekDates[0].getFullYear();

      // Fetch approved absences (status: 1 = Approved)
      const data = await shortAbsenceService.getAll({ year, status: 1 });
      setAbsences(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load short absences:', e);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [weekDates]);

  // Load history data based on filters
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const filters = {};
      if (historyYear) filters.year = historyYear;
      if (historyMonth) filters.month = historyMonth;
      if (selectedEmployee) filters.employeeId = selectedEmployee.id || selectedEmployee.Id;
      const data = await shortAbsenceService.getAll(filters);
      setHistoryData(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load history:', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyYear, historyMonth, selectedEmployee]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Build absence map: date -> array of absences
  const absenceMap = useMemo(() => {
    const map = {};
    absences.forEach(a => {
      const key = a.date;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [absences]);

  // Get all absences for a specific day and hour slot
  const getAbsencesForSlot = (dayIndex, hour) => {
    const dateStr = formatDate(weekDates[dayIndex]);
    const dayAbsences = absenceMap[dateStr] || [];
    return dayAbsences.filter(a => {
      const [sh] = a.startTime.split(':').map(Number);
      const [eh] = a.endTime.split(':').map(Number);
      return hour >= sh && hour < eh;
    });
  };

  const today = formatDate(new Date());

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={40} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Short Absence Map</Typography>
      
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => setWeekOffset(w => w - 1)}><ChevronLeftIcon /></IconButton>
        <IconButton size="small" onClick={() => setWeekOffset(0)}><TodayIcon /></IconButton>
        <IconButton size="small" onClick={() => setWeekOffset(w => w + 1)}><ChevronRightIcon /></IconButton>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {formatShortDate(weekDates[0])} - {formatShortDate(weekDates[4])}
        </Typography>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: '50px repeat(5, 1fr)', userSelect: 'none' }}>
        {/* Header row */}
        <Box />
        {weekDates.map((d, i) => (
          <Box key={i} sx={{ textAlign: 'center', py: 0.5, fontWeight: formatDate(d) === today ? 'bold' : 'normal',
            bgcolor: formatDate(d) === today ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
            <Typography variant="caption">{WEEKDAYS[i]}</Typography>
            <Typography variant="body2">{d.getDate()}</Typography>
          </Box>
        ))}

        {/* Time slots */}
        {HOURS.map(hour => (
          <React.Fragment key={hour}>
            <Box sx={{ pr: 1, textAlign: 'right', fontSize: '0.75rem', color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {`${hour}:00`}
            </Box>
            {weekDates.map((_, dayIndex) => {
              const slotAbsences = getAbsencesForSlot(dayIndex, hour);
              const hasAbsences = slotAbsences.length > 0;
              const names = slotAbsences.map(a => a.employeeName).join(', ');
              return (
                <Tooltip key={dayIndex} title={hasAbsences ? names : ''}>
                  <Box sx={{
                    minHeight: 28,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: hasAbsences ? alpha(theme.palette.warning.main, 0.3) : 'background.paper',
                    p: 0.25,
                    overflow: 'hidden'
                  }}>
                    {hasAbsences && (
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.1, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {slotAbsences.map(a => a.employeeName.split(' ')[0]).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      {/* History Section */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>Short Absence History</Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Autocomplete
          size="small"
          sx={{ minWidth: 250 }}
          options={employees}
          value={selectedEmployee}
          onChange={(_, newValue) => setSelectedEmployee(newValue)}
          getOptionLabel={(option) =>
            option.fullName ||
            `${option.person?.firstName || ''} ${option.person?.lastName || ''}`.trim() ||
            `Employee ${option.id}`
          }
          isOptionEqualToValue={(option, value) => (option.id || option.Id) === (value.id || value.Id)}
          renderInput={(params) => <TextField {...params} label="Employee" placeholder="Search employee..." />}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={historyMonth}
            onChange={(e) => setHistoryMonth(e.target.value)}
            label="Month"
          >
            <MenuItem value="">All Months</MenuItem>
            {MONTHS.map((m, i) => (
              <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={historyYear}
            onChange={(e) => setHistoryYear(e.target.value)}
            label="Year"
          >
            <MenuItem value="">All Years</MenuItem>
            {[currentYear - 1, currentYear, currentYear + 1].map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* History Table */}
      {historyLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={30} />
        </Box>
      ) : historyData.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No short absences found for the selected period.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyData.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.employeeName}</TableCell>
                  <TableCell>{a.date}</TableCell>
                  <TableCell>{a.startTime} - {a.endTime}</TableCell>
                  <TableCell>{a.durationHours}h</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap title={a.note}>
                      {a.note || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={a.statusText || a.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

